// src/pages/analytics/Dashboard.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  Tabs,
  Tab,
  Divider,
  useTheme,
  alpha,
  Breadcrumbs,
  Paper,
  Fade,
  Grow,
  Zoom,
  Alert,
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  TrendingUp as TrendingUpIcon,
  Campaign as CampaignIcon,
  Article as ContentIcon,
  People as ClientsIcon,
  Business as BrandsIcon,
  Public as SocialIcon,
  Visibility as ViewsIcon,
  ThumbUp as EngagementIcon,
  PermIdentity as ReachIcon,
  FileDownload as DownloadIcon,
  NavigateNext as NavigateNextIcon,
  DateRange as DateRangeIcon,
  People,
  Insights as InsightsIcon,
  Analytics as AnalyticsIcon,
  Timeline as TimelineIcon,
  Bolt as BoltIcon,
  Dashboard as DashboardIcon,
  AutoAwesome as MagicIcon,
} from '@mui/icons-material';
import { 
  ResponsiveContainer, 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  PieChart, 
  Pie, 
  Cell 
} from 'recharts';

// Import custom components
import { MagicLoader } from '../../design-system';
import { PageContainer } from '../../components/ui';
import KPICard from '../../components/features/analytics/KPICard';
import ChartCard from '../../components/features/analytics/ChartCard';
import DateRangePicker from '../../components/ui/DateRangePicker';
import MetricComparison from '../../components/features/analytics/MetricComparison';
import ExportButton from '../../components/features/analytics/ExportButton';
import AnalyticsFilters from '../../components/features/analytics/AnalyticsFilters';

// Import store
import useAnalyticsStore from '../../stores/analyticsStore';
import useNotificationStore from '../../stores/notificationStore';
import CampaignAnalytics from './CampaignAnalytics';
import ContentAnalytics from './ContentAnalytics';
import FinancialSummary from './components/FinancialSummary';
import ClientAnalytics from '../clients/ClientAnalytics';
import UnifiedTaskList from '../../components/tasks/UnifiedTaskList';

// Import services
import { exportDashboardAnalytics } from '../../services/exportService';

// Import responsive styles
import { 
  getKpiGridProps, 
  getChartGridProps, 
  getPageHeaderStyles,
  getTabsContainerStyles,
  getResponsiveChartHeight
} from '../../styles/analyticsResponsive';

/**
 * Analytics Dashboard Page Component
 */
const AnalyticsDashboard = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  
  // Define filter state
  const [filters, setFilters] = useState({
    platform: [],
    contentType: 'all',
    showComparisonPeriod: false,
    campaignId: 'all',
    brandId: 'all',
    clientId: 'all',
  });
  
  const { 
    dashboardData, 
    isLoading, 
    error, 
    fetchDashboardAnalytics,
    dateRange,
    setDateRange,
    exportAnalyticsData,
    clearError,
  } = useAnalyticsStore();
  
  const { showError } = useNotificationStore();
  
  // Define filter options
  const filterOptions = [
    {
      key: 'platform',
      label: 'Platform',
      type: 'multi-select',
      options: [
        { value: 'facebook', label: 'Facebook' },
        { value: 'instagram', label: 'Instagram' },
        { value: 'twitter', label: 'Twitter' },
        { value: 'linkedin', label: 'LinkedIn' },
        { value: 'youtube', label: 'YouTube' },
        { value: 'tiktok', label: 'TikTok' },
      ],
      defaultValue: [],
      helperText: 'Filter by social media platform',
    },
    {
      key: 'contentType',
      label: 'Content Type',
      type: 'select',
      options: [
        { value: 'all', label: 'All Types' },
        { value: 'posts', label: 'Posts' },
        { value: 'stories', label: 'Stories' },
        { value: 'reels', label: 'Reels' },
        { value: 'videos', label: 'Videos' },
        { value: 'carousels', label: 'Carousels' },
      ],
      defaultValue: 'all',
    },
    {
      key: 'showComparisonPeriod',
      label: 'Show Comparison Period',
      type: 'checkbox',
      defaultValue: false,
      helperText: 'Compare to previous period of equal length',
    },
    {
      key: 'campaignId',
      label: 'Campaign',
      type: 'select',
      options: [
        { value: 'all', label: 'All Campaigns' },
        { value: 'campaign1', label: 'Summer Campaign 2023' },
        { value: 'campaign2', label: 'Product Launch Q2' },
        { value: 'campaign3', label: 'Holiday Special' },
      ],
      defaultValue: 'all',
    },
  ];
  
  // Fetch dashboard data on component mount or when filters change
  useEffect(() => {
    const loadData = async () => {
      try {
        // Prepare filter parameters
        const params = {
          ...filters,
          // Convert array to comma-separated string for API
          platform: filters.platform.length > 0 ? filters.platform.join(',') : undefined,
          // Only include non-default filters
          contentType: filters.contentType !== 'all' ? filters.contentType : undefined,
          campaignId: filters.campaignId !== 'all' ? filters.campaignId : undefined,
          brandId: filters.brandId !== 'all' ? filters.brandId : undefined,
          clientId: filters.clientId !== 'all' ? filters.clientId : undefined,
          includeComparisonPeriod: filters.showComparisonPeriod || undefined,
        };
        
        await fetchDashboardAnalytics(params);
      } catch (error) {
        showError('Failed to load dashboard analytics: ' + (error.message || 'Unknown error'));
      }
    };
    
    loadData();
  }, [fetchDashboardAnalytics, showError, dateRange, filters]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Handle date range change
  const handleDateRangeChange = (startDate, endDate) => {
    setDateRange(startDate, endDate);
  };
  
  // Handle filter change
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };
  
  // Handle export analytics
  const handleExportAnalytics = async (format = 'excel') => {
    try {
      const { startDate, endDate } = dateRange;
      
      // Prepare filter parameters for export
      const exportParams = {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        format: format,
        // Add filter parameters
        platform: filters.platform.length > 0 ? filters.platform.join(',') : undefined,
        contentType: filters.contentType !== 'all' ? filters.contentType : undefined,
        campaignId: filters.campaignId !== 'all' ? filters.campaignId : undefined,
        brandId: filters.brandId !== 'all' ? filters.brandId : undefined,
        clientId: filters.clientId !== 'all' ? filters.clientId : undefined,
        includeComparisonPeriod: filters.showComparisonPeriod || undefined,
      };
      
      // Use the export service
      await exportDashboardAnalytics(exportParams);
    } catch (error) {
      showError('Failed to export analytics data: ' + (error.message || 'Unknown error'));
      throw error; // Rethrow to be caught by ExportButton
    }
  };
  
  // Chart colors
  const chartColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
  ];
  
  // Check if data is available
  const hasData = !isLoading && dashboardData;
  
  // Loading state
  if (isLoading && !dashboardData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <MagicLoader variant="mystical" size="large" message="Loading analytics dashboard data..." />
      </Box>
    );
  }
  
  
  // Empty state
  if (!isLoading && (!dashboardData || Object.keys(dashboardData).length === 0)) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '70vh', gap: 2 }}>
        <Typography variant="h6">No analytics data available</Typography>
        <Typography variant="body2" color="text.secondary">
          There is no data available for the selected filters. Please adjust your filters or try again later.
        </Typography>
        <Box sx={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <AnalyticsFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
            filterOptions={filterOptions}
            showDateRange={true}
          />
          <Button 
            variant="contained" 
            onClick={() => fetchDashboardAnalytics()}
            sx={{ alignSelf: 'center' }}
          >
            Refresh Data
          </Button>
        </Box>
      </Box>
    );
  }
  // For development/testing - comment out when real data is available
  // return(
  //   <TaskList viewMode="all" />
  // )
  return (
    <PageContainer>
      {/* Page header */}
      <Fade in={true} timeout={800}>
        <Box sx={{ mb: 4 }}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              mb: 3, 
              borderRadius: theme?.aghrba?.tokens?.borderRadius?.card || 16,
              background: theme.palette.mode === 'dark' 
                ? `linear-gradient(45deg, ${alpha(theme.palette.primary.dark, 0.6)}, ${alpha(theme.palette.secondary.dark, 0.6)})`
                : `linear-gradient(45deg, ${alpha(theme.palette.primary.light, 0.1)}, ${alpha(theme.palette.secondary.light, 0.1)})`,
              position: 'relative',
              overflow: 'hidden',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.divider, theme?.aghrba?.tokens?.alpha?.border || 0.1)}`,
              transition: theme?.aghrba?.tokens?.transitions?.standard || 'all 0.3s ease',
            }}
          >
            {/* Abstract shape for decoration */}
            <Box 
              sx={{ 
                position: 'absolute', 
                top: -50, 
                right: -50, 
                width: 200, 
                height: 200, 
                borderRadius: '50%', 
                background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)}, transparent 70%)`,
                zIndex: 0,
              }} 
            />
            <Box 
              sx={{ 
                position: 'absolute', 
                bottom: -30, 
                left: -30, 
                width: 150, 
                height: 150, 
                borderRadius: '50%', 
                background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.1)}, transparent 70%)`,
                zIndex: 0,
              }} 
            />
            
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                sx={{ mb: 2 }}
              >
                <Typography color="text.primary">Analytics</Typography>
                <Typography color="text.primary">Dashboard</Typography>
              </Breadcrumbs>
              
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' }, 
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', md: 'center' },
                gap: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <DashboardIcon 
                    color="primary" 
                    sx={{ 
                      fontSize: 40,
                      p: 0.8,
                      borderRadius: theme?.aghrba?.tokens?.borderRadius?.medium || 8,
                      background: theme.palette.mode === 'dark' 
                        ? alpha(theme.palette.primary.main, 0.15)
                        : alpha(theme.palette.primary.main, 0.1),
                    }} 
                  />
                  <Box>
                    <Typography variant="h4" component="h1" sx={{ 
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      background: theme.palette.mode === 'dark'
                        ? `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                        : `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}>
                      Analytics Dashboard <MagicIcon fontSize="small" sx={{ ml: 1, opacity: 0.7 }} />
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.5 }}>
                      Comprehensive insights for data-driven decisions
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2, mt: { xs: 2, md: 0 }, flexWrap: 'wrap' }}>
                  <AnalyticsFilters
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    dateRange={dateRange}
                    onDateRangeChange={handleDateRangeChange}
                    filterOptions={filterOptions}
                    showDateRange={true}
                    allowSaveFilters={true}
                    presetFilterSets={[
                      {
                        id: 'last30days',
                        name: 'Last 30 Days',
                        filters: {
                          platform: [],
                          contentType: 'all',
                          showComparisonPeriod: true,
                          campaignId: 'all'
                        }
                      },
                      {
                        id: 'topPerformers',
                        name: 'Top Performers',
                        filters: {
                          platform: ['instagram', 'facebook'],
                          contentType: 'all',
                          showComparisonPeriod: true,
                          campaignId: 'all'
                        }
                      }
                    ]}
                  />
                  
                  <ExportButton
                    onExport={handleExportAnalytics}
                    formats={['excel', 'pdf', 'csv']}
                    buttonText="Export Report"
                    size="small"
                  />
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Fade>
      
      {/* Error message */}
      {error && dashboardData && (
        <Box sx={{ mb: 2 }}>
          <Alert 
            severity="error" 
            onClose={() => clearError()}
          >
            {error}
          </Alert>
        </Box>
      )}
      
      {/* Overview KPIs */}
      <Box sx={{ mb: 4, overflow: 'hidden' }}>
        <Grid container {...getKpiGridProps(theme).container}>
          <Grid item xs={12} sm={6} md={3}>
            <Zoom in={true} style={{ transitionDelay: '100ms' }}>
              <Box>
                <KPICard
                  title="Total Campaigns"
                  value={hasData ? dashboardData.campaigns?.total : null}
                  previousValue={hasData ? dashboardData.campaigns?.previousTotal : null}
                  percentChange={hasData ? dashboardData.campaigns?.percentChange : null}
                  icon={<CampaignIcon />}
                  isLoading={isLoading}
                  magical
                  level={2}
                  tooltip="Total number of active marketing campaigns"
                  footerText={hasData && dashboardData.campaigns?.total ? `Last updated: ${new Date().toLocaleDateString()}` : null}
                />
              </Box>
            </Zoom>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Zoom in={true} style={{ transitionDelay: '200ms' }}>
              <Box>
                <KPICard
                  title="Total Content"
                  value={hasData ? dashboardData.content?.total : null}
                  previousValue={hasData ? dashboardData.content?.previousTotal : null}
                  percentChange={hasData ? dashboardData.content?.percentChange : null}
                  icon={<ContentIcon />}
                  color="secondary"
                  isLoading={isLoading}
                  magical
                  level={2}
                  tooltip="Total content pieces across all platforms"
                  footerText={hasData && dashboardData.content?.total ? `${Math.round(dashboardData.content?.total * 0.8)} published, ${Math.round(dashboardData.content?.total * 0.2)} draft` : null}
                />
              </Box>
            </Zoom>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Zoom in={true} style={{ transitionDelay: '300ms' }}>
              <Box>
                <KPICard
                  title="Total Reach"
                  value={hasData ? dashboardData.reach?.total : null}
                  previousValue={hasData ? dashboardData.reach?.previousTotal : null}
                  percentChange={hasData ? dashboardData.reach?.percentChange : null}
                  icon={<ReachIcon />}
                  color="info"
                  isLoading={isLoading}
                  magical
                  level={2}
                  tooltip="Unique users reached across all platforms"
                  footerText={hasData && dashboardData.reach?.total ? `Avg. ${Math.round(dashboardData.reach?.total / 30).toLocaleString()} daily reach` : null}
                />
              </Box>
            </Zoom>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Zoom in={true} style={{ transitionDelay: '400ms' }}>
              <Box>
                <KPICard
                  title="Total Engagement"
                  value={hasData ? dashboardData.engagement?.total : null}
                  previousValue={hasData ? dashboardData.engagement?.previousTotal : null}
                  percentChange={hasData ? dashboardData.engagement?.percentChange : null}
                  icon={<EngagementIcon />}
                  color="success"
                  isLoading={isLoading}
                  magical
                  level={2}
                  tooltip="Total likes, comments, shares and clicks"
                  footerText={hasData && dashboardData.engagement?.total && dashboardData.reach?.total 
                    ? `${((dashboardData.engagement?.total / dashboardData.reach?.total) * 100).toFixed(1)}% engagement rate` 
                    : null}
                />
              </Box>
            </Zoom>
          </Grid>
        </Grid>
      </Box>
      
      {/* Content tabs */}
      <Box sx={{ mb: 3 }}>
        <Paper 
          elevation={0} 
          sx={{ 
            p: 0.5, 
            borderRadius: theme?.aghrba?.tokens?.borderRadius?.large || 12,
            background: theme.palette.mode === 'dark' 
              ? alpha(theme.palette.background.paper, 0.4)
              : alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(8px)',
            border: `1px solid ${alpha(theme.palette.divider, theme?.aghrba?.tokens?.alpha?.border || 0.1)}`,
            boxShadow: `0 4px 20px 0 ${alpha(theme.palette.common.black, 0.05)}`,
            overflow: 'hidden',
            transition: theme?.aghrba?.tokens?.transitions?.standard || 'all 0.3s ease',
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            sx={{
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                background: theme.palette.mode === 'dark'
                  ? `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                  : `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              },
              '& .MuiTab-root': {
                minHeight: 56,
                fontWeight: 500,
                transition: theme.transitions.create(['color', 'background'], {
                  duration: theme.transitions.duration.short,
                }),
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  background: alpha(theme.palette.primary.main, 0.05),
                },
                '&:hover': {
                  background: alpha(theme.palette.primary.main, 0.03),
                },
              }
            }}
            TabIndicatorProps={{
              children: <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  background: theme.palette.mode === 'dark'
                    ? `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                    : `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                }}
              />
            }}
          >
            <Tab 
              icon={<InsightsIcon />} 
              iconPosition="start" 
              label="Performance" 
              sx={{ px: 3, borderRadius: theme?.aghrba?.tokens?.borderRadius?.large || 12 }}
            />
            <Tab 
              icon={<CampaignIcon />} 
              iconPosition="start" 
              label="Campaigns" 
              sx={{ px: 3, borderRadius: theme?.aghrba?.tokens?.borderRadius?.large || 12 }}
            />
            <Tab 
              icon={<ContentIcon />} 
              iconPosition="start" 
              label="Content" 
              sx={{ px: 3, borderRadius: theme?.aghrba?.tokens?.borderRadius?.large || 12 }}
            />
            <Tab 
              icon={<SocialIcon />} 
              iconPosition="start" 
              label="Social Media" 
              sx={{ px: 3, borderRadius: theme?.aghrba?.tokens?.borderRadius?.large || 12 }}
            />
          </Tabs>
        </Paper>
      </Box>
      
      {/* Performance Tab */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
            {/* Performance Over Time Chart */}
            <Grid item {...getChartGridProps(theme, 'large')}>
                <ChartCard
                    title="Performance Trends"
                    subtitle="Reach and engagement metrics over time"
                    icon={<TimelineIcon color="primary" />}
                    isLoading={isLoading}
                    height={400}
                    magical
                    level={2}
                    actions={[
                      { label: 'View Detailed Report', onClick: () => {} },
                      { label: 'Share Insights', onClick: () => {} }
                    ]}
                  >
                    {hasData && dashboardData.performanceTrends && (
                      <ResponsiveContainer width="100%" height={350}>
                        <LineChart
                          data={dashboardData.performanceTrends}
                          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        >
                          <defs>
                            <linearGradient id="colorReach" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3}/>
                              <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="colorEngagement" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={theme.palette.secondary.main} stopOpacity={0.3}/>
                              <stop offset="95%" stopColor={theme.palette.secondary.main} stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="colorImpressions" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={theme.palette.info.main} stopOpacity={0.3}/>
                              <stop offset="95%" stopColor={theme.palette.info.main} stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.2)} />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                            stroke={alpha(theme.palette.divider, 0.5)}
                            tickLine={false}
                            axisLine={false}
                          />
                          <YAxis 
                            yAxisId="left"
                            tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                            stroke={alpha(theme.palette.divider, 0.5)}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(value) => value >= 1000 ? `${value/1000}K` : value}
                          />
                          <YAxis 
                            yAxisId="right" 
                            orientation="right"
                            tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                            stroke={alpha(theme.palette.divider, 0.5)}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(value) => value >= 1000 ? `${value/1000}K` : value}
                          />
                          <RechartsTooltip 
                            contentStyle={{ 
                              backgroundColor: theme.palette.background.paper,
                              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                              borderRadius: 8,
                              boxShadow: `0 4px 20px 0 ${alpha(theme.palette.common.black, 0.1)}`,
                              padding: 12,
                              fontSize: 12,
                            }}
                            formatter={(value, name) => [`${value.toLocaleString()}`, name]}
                            labelFormatter={(label) => `Date: ${label}`}
                          />
                          <Legend 
                            verticalAlign="top" 
                            height={36}
                            iconType="circle"
                            iconSize={10}
                            wrapperStyle={{
                              fontSize: 12,
                              fontWeight: 500,
                            }}
                          />
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="reach"
                            name="Reach"
                            stroke={theme.palette.primary.main}
                            fill="url(#colorReach)"
                            fillOpacity={1}
                            activeDot={{ r: 6, stroke: theme.palette.primary.main, strokeWidth: 3, fill: theme.palette.common.white }}
                            strokeWidth={3}
                            dot={{ stroke: theme.palette.primary.main, strokeWidth: 2, r: 4, fill: theme.palette.common.white }}
                          />
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="engagement"
                            name="Engagement"
                            stroke={theme.palette.secondary.main}
                            fill="url(#colorEngagement)"
                            fillOpacity={1}
                            activeDot={{ r: 6, stroke: theme.palette.secondary.main, strokeWidth: 3, fill: theme.palette.common.white }}
                            strokeWidth={3}
                            dot={{ stroke: theme.palette.secondary.main, strokeWidth: 2, r: 4, fill: theme.palette.common.white }}
                          />
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="impressions"
                            name="Impressions"
                            stroke={theme.palette.info.main}
                            fill="url(#colorImpressions)"
                            fillOpacity={1}
                            activeDot={{ r: 6, stroke: theme.palette.info.main, strokeWidth: 3, fill: theme.palette.common.white }}
                            strokeWidth={3}
                            dot={{ stroke: theme.palette.info.main, strokeWidth: 2, r: 4, fill: theme.palette.common.white }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </ChartCard>
            </Grid>
          
          {/* Platform Distribution */}
          <Grid item xs={12} sm={6} lg={4}>
                <ChartCard
                  title="Platform Distribution"
                  subtitle="Engagement by platform"
                  icon={<SocialIcon color="secondary" />}
                  isLoading={isLoading}
                  height={400}
                  color="secondary"
                  magical
                  level={2}
                  tooltip="Shows the percentage of engagement across social media platforms"
                >
                  {hasData && dashboardData.platformDistribution && (
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={dashboardData.platformDistribution}
                          cx="50%"
                          cy="50%"
                          innerRadius={70}
                          outerRadius={110}
                          paddingAngle={3}
                          dataKey="value"
                          nameKey="platform"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          labelLine={true}
                          animationDuration={1500}
                          animationBegin={200}
                        >
                          {dashboardData.platformDistribution.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={chartColors[index % chartColors.length]}
                              strokeWidth={1.5}
                              stroke={theme.palette.background.paper}
                            />
                          ))}
                        </Pie>
                        <RechartsTooltip 
                          formatter={(value, name, props) => [`${value}%`, props.payload.platform]}
                          contentStyle={{ 
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                            borderRadius: 8,
                            boxShadow: `0 4px 20px 0 ${alpha(theme.palette.common.black, 0.1)}`,
                            padding: 12,
                            fontSize: 12,
                          }}
                        />
                        <Legend 
                          layout="horizontal" 
                          verticalAlign="bottom" 
                          align="center"
                          iconType="circle"
                          iconSize={10}
                          wrapperStyle={{
                            fontSize: 12,
                            fontWeight: 500,
                            padding: '8px 0',
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
          </Grid>
          
          {/* Content Performance */}
          <Grid item xs={12} sm={6} lg={4}>
            <Grow in={true} timeout={1000} style={{ transitionDelay: '200ms' }}>
              <Box>
                <ChartCard
                  title="Top Performing Content"
                  subtitle="By engagement rate"
                  icon={<ContentIcon color="success" />}
                  isLoading={isLoading}
                  height={400}
                  color="success"
                  magical
                  level={2}
                  tooltip="Content with the highest engagement rates"
                  onRefresh={() => fetchDashboardAnalytics()}
                >
                  {hasData && dashboardData.topContent && (
                    <Box sx={{ 
                      height: 350, 
                      overflowY: 'auto',
                      pr: 1,
                      '&::-webkit-scrollbar': {
                        width: '6px',
                        borderRadius: '3px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: alpha(theme.palette.divider, 0.1),
                        borderRadius: '3px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: alpha(theme.palette.primary.main, 0.2),
                        borderRadius: '3px',
                        '&:hover': {
                          background: alpha(theme.palette.primary.main, 0.3),
                        },
                      },
                    }}>
                      {dashboardData.topContent.map((content, index) => (
                        <Paper
                          key={index}
                          elevation={0}
                          sx={{
                            mb: 2,
                            p: 2,
                            borderRadius: theme.shape.borderRadius,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            background: index === 0 
                              ? alpha(theme.palette.success.main, 0.05) 
                              : 'transparent',
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'transform 0.3s, box-shadow 0.3s',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: `0 4px 12px 0 ${alpha(theme.palette.common.black, 0.05)}`,
                            },
                          }}
                        >
                          {index === 0 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                background: theme.palette.success.main,
                                color: theme.palette.common.white,
                                fontSize: 10,
                                fontWeight: 'bold',
                                px: 1,
                                py: 0.5,
                                borderRadius: '0 0 0 4px',
                                textTransform: 'uppercase',
                              }}
                            >
                              Best
                            </Box>
                          )}
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2" fontWeight={600} noWrap sx={{ flexGrow: 1 }}>
                              {content.title}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 600, 
                                color: theme.palette.success.main,
                                background: alpha(theme.palette.success.main, 0.1),
                                borderRadius: '4px',
                                px: 1,
                                py: 0.25,
                              }}
                            >
                              {content.engagementRate}%
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="textSecondary" sx={{ mb: 1.5, display: 'block' }}>
                            {content.platform} • {content.type} • {content.date}
                          </Typography>
                          <MetricComparison
                            title={`${content.platform} Average`}
                            currentValue={content.engagement}
                            targetValue={dashboardData.benchmarks?.avgEngagement || 100}
                            tooltip="Compared to platform average"
                            color="success"
                          />
                        </Paper>
                      ))}
                    </Box>
                  )}
                </ChartCard>
              </Box>
            </Grow>
          </Grid>
          
          {/* Campaign Performance */}
          <Grid item xs={12} sm={6} lg={4}>
            <Grow in={true} timeout={1200} style={{ transitionDelay: '300ms' }}>
              <Box>
                <ChartCard
                  title="Top Performing Campaigns"
                  subtitle="By goal completion"
                  icon={<CampaignIcon color="primary" />}
                  isLoading={isLoading}
                  height={400}
                  color="primary"
                  magical
                  level={2}
                  tooltip="Campaigns with highest goal completion rates"
                  onRefresh={() => fetchDashboardAnalytics()}
                >
                  {hasData && dashboardData.topCampaigns && (
                    <Box sx={{ 
                      height: 350, 
                      overflowY: 'auto',
                      pr: 1,
                      '&::-webkit-scrollbar': {
                        width: '6px',
                        borderRadius: '3px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: alpha(theme.palette.divider, 0.1),
                        borderRadius: '3px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: alpha(theme.palette.primary.main, 0.2),
                        borderRadius: '3px',
                        '&:hover': {
                          background: alpha(theme.palette.primary.main, 0.3),
                        },
                      },
                    }}>
                      {dashboardData.topCampaigns.map((campaign, index) => (
                        <Paper
                          key={index}
                          elevation={0}
                          sx={{
                            mb: 2,
                            p: 2,
                            borderRadius: theme.shape.borderRadius,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            background: index === 0 
                              ? alpha(theme.palette.primary.main, 0.05) 
                              : 'transparent',
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'transform 0.3s, box-shadow 0.3s',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: `0 4px 12px 0 ${alpha(theme.palette.common.black, 0.05)}`,
                            },
                          }}
                        >
                          {index === 0 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                background: theme.palette.primary.main,
                                color: theme.palette.common.white,
                                fontSize: 10,
                                fontWeight: 'bold',
                                px: 1,
                                py: 0.5,
                                borderRadius: '0 0 0 4px',
                                textTransform: 'uppercase',
                              }}
                            >
                              Top
                            </Box>
                          )}
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2" fontWeight={600} noWrap sx={{ flexGrow: 1 }}>
                              {campaign.name}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 600, 
                                color: campaign.goalCompletion > 80 
                                  ? theme.palette.success.main 
                                  : campaign.goalCompletion > 50 
                                  ? theme.palette.warning.main 
                                  : theme.palette.error.main,
                                background: campaign.goalCompletion > 80 
                                  ? alpha(theme.palette.success.main, 0.1) 
                                  : campaign.goalCompletion > 50 
                                  ? alpha(theme.palette.warning.main, 0.1) 
                                  : alpha(theme.palette.error.main, 0.1),
                                borderRadius: '4px',
                                px: 1,
                                py: 0.25,
                              }}
                            >
                              {campaign.goalCompletion}%
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="textSecondary" sx={{ mb: 1.5, display: 'block' }}>
                            {campaign.type} • Budget: ${campaign.budget?.toLocaleString()} • Ends: {campaign.endDate}
                          </Typography>
                          <MetricComparison
                            title="Goal Completion"
                            currentValue={campaign.goalCompletion}
                            targetValue={100}
                            isPercentage={true}
                            color={campaign.goalCompletion > 80 
                              ? "success" 
                              : campaign.goalCompletion > 50 
                              ? "warning" 
                              : "error"}
                          />
                        </Paper>
                      ))}
                    </Box>
                  )}
                </ChartCard>
              </Box>
            </Grow>
          </Grid>
          
          {/* Audience Demographics */}
          <Grid item xs={12} sm={6} lg={4}>
            <Grow in={true} timeout={1400} style={{ transitionDelay: '400ms' }}>
              <Box>
                <ChartCard
                  title="Audience Demographics"
                  subtitle="Age group distribution"
                  icon={<People />}
                  isLoading={isLoading}
                  height={400}
                  color="info"
                >
                  {hasData && dashboardData.audienceDemographics && (
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart
                        data={dashboardData.audienceDemographics}
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                        <XAxis 
                          dataKey="ageGroup" 
                          tick={{ fill: theme.palette.text.secondary }}
                          stroke={theme.palette.divider}
                        />
                        <YAxis 
                          tick={{ fill: theme.palette.text.secondary }}
                          stroke={theme.palette.divider}
                        />
                        <RechartsTooltip 
                          contentStyle={{ 
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 8
                          }}
                        />
                        <Legend />
                        <Bar 
                          dataKey="male" 
                          name="Male" 
                          fill={theme.palette.primary.main} 
                          barSize={20}
                        />
                        <Bar 
                          dataKey="female" 
                          name="Female" 
                          fill={theme.palette.secondary.main} 
                          barSize={20}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
              </Box>
            </Grow>
          </Grid>
        </Grid>
      )}
      
      {/* Campaigns Tab */}
      {tabValue === 1 && (
        <CampaignAnalytics/>
        // <Typography>Campaign analytics tab content</Typography>
        // Campaign analytics will be implemented separately
      )}
      
      {/* Content Tab */}
      {tabValue === 2 && (
        <ContentAnalytics/>
        // <Typography>Content analytics tab content</Typography>
        // Content analytics will be implemented separately
      )}
      
      {/* Social Media Tab */}
      {tabValue === 3 && (
        <>
          <FinancialSummary/>
          <ClientAnalytics/>
          {/* Social media analytics will be implemented separately */}
        </>
      )}
    </PageContainer>
  );
};

export default AnalyticsDashboard;